// src/app/hooks/useLogin.ts
import { loginMessage } from "app/config";
import type { AuthTokens } from "app/types/auth-tokens";
import { useCallback, useState } from "react";
import tokenService from "services/token.service";
import { useAuthenticateMutation } from "store/api/new.api";
import { useAccount, useChainId } from "wagmi";
import { useWallet } from "@solana/wallet-adapter-react";
import { useAuthLogger, useErrorLogger } from "app/hooks/useLogger";
import { WalletType } from "app/types/logging.types";
import { useSafeSignMessage } from "./useSafeSignMessage";
import { useTokenValidation } from "./useTokenValidation";

export type LoginErrorType =
  | "user_rejected"
  | "request_pending"
  | "solana_required"
  | "network_switch"
  | "token_validation"
  | "auth_request"
  | "unknown";

export type LoginResult = {
  success: boolean;
  errorType?: LoginErrorType;
  message?: string;
  error?: unknown;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
};

const getErrorCode = (error: unknown): number | undefined => {
  if (error && typeof error === "object" && "code" in error) {
    return Number((error as { code?: number }).code);
  }

  return undefined;
};

const getErrorDetails = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === "number" ? status : undefined;
  }

  return undefined;
};

const isLegacyAuthPayloadError = (error: unknown): boolean => {
  const details = getErrorDetails(error).toLowerCase();

  return (
    getErrorStatus(error) === 400 &&
    (details.includes("signature") || details.includes("message")) &&
    (details.includes("should not exist") ||
      details.includes("unknown") ||
      details.includes("unexpected") ||
      details.includes("not allowed"))
  );
};

const classifyLoginError = (error: unknown): LoginErrorType => {
  const message = getErrorMessage(error).toLowerCase();
  const code = getErrorCode(error);

  if (
    code === 4001 ||
    message.includes("user rejected") ||
    message.includes("rejected the request")
  ) {
    return "user_rejected";
  }

  if (
    code === -32002 ||
    message.includes("already pending") ||
    message.includes("request already pending")
  ) {
    return "request_pending";
  }

  if (message.includes("switch") || message.includes("chain")) {
    return "network_switch";
  }

  if (getErrorStatus(error)) {
    return "auth_request";
  }

  return "unknown";
};

export const useLogin = () => {
  const [authenticate, { isLoading: isAuthLoading, error: authError }] =
    useAuthenticateMutation();

  const chainId = useChainId();
  const { connector } = useAccount();
  const {
    signMessageAsync,
    status: signStatus,
    error: signError,
  } = useSafeSignMessage();

  const { connected: solanaConnected } = useWallet();
  const [solanaWarning, setSolanaWarning] = useState<string | null>(null);
  const { validateToken } = useTokenValidation();

  // Logging hooks
  const { logLoginAttempt, logLoginSuccess, logLoginError, logMessageSign } =
    useAuthLogger();
  const { logJavaScriptError } = useErrorLogger();

  const isMessageSigning = signStatus === "pending";
  const isLoading = isAuthLoading || isMessageSigning;
  const error = authError || signError;

  const loginMutation = useCallback(
    async (
      address: string,
      checkSolana: boolean = false
    ): Promise<LoginResult> => {
      try {
        // Log login attempt
        logLoginAttempt("metamask" as WalletType, {
          address,
          checkSolana,
          solanaConnected,
          currentChainId: chainId,
        });

        // Check if Solana wallet is required and connected
        if (checkSolana && !solanaConnected) {
          const error = new Error("Solana wallet required but not connected");
          logLoginError("metamask" as WalletType, error, {
            address,
            reason: "solana_wallet_required",
            checkSolana,
            solanaConnected,
          });

          setSolanaWarning(
            "Please connect your Solana wallet before logging in"
          );
          return {
            success: false,
            errorType: "solana_required",
            message: error.message,
          };
        }

        setSolanaWarning(null);

        // Log message signing attempt
        logMessageSign("metamask" as WalletType, true, {
          address,
          message: loginMessage,
          chainId,
        });

        const signature = await signMessageAsync({
          address,
          connector,
          message: loginMessage,
        });

        // Log successful message signing (already logged above with success=true)
        // logMessageSigningSuccess('metamask' as WalletType, {
        //   address,
        //   message: loginMessage,
        //   signatureLength: signature.length
        // });

        localStorage.setItem("signature", signature);

        let response;

        try {
          response = await authenticate({
            address,
            signature,
            message: loginMessage,
          }).unwrap();
        } catch (authError) {
          if (!isLegacyAuthPayloadError(authError)) {
            throw authError;
          }

          console.warn(
            "[useLogin] Auth endpoint rejected signed payload shape, retrying legacy address-only auth"
          );
          response = await authenticate({ address }).unwrap();
        }

        const authTokens: AuthTokens = {
          accessToken: response.accessToken,
          user: response.user,
        };
        tokenService.setTokens(authTokens);

        // Validate the new token immediately after login
        console.log("[useLogin] Validating newly received token");
        const validationResult = await validateToken(response.accessToken);

        if (!validationResult.valid && !validationResult.requestFailed) {
          console.error(
            "[useLogin] Token validation failed immediately after login:",
            validationResult.error
          );
          tokenService.removeToken();
          logLoginError(
            "metamask" as WalletType,
            new Error("Token validation failed"),
            {
              address,
              reason: "invalid_token_after_login",
              validationError: validationResult.error,
            }
          );
          return {
            success: false,
            errorType: "token_validation",
            message: validationResult.error || "Token validation failed",
          };
        }

        if (validationResult.requestFailed) {
          console.warn(
            "[useLogin] Token validation unavailable after login, keeping fresh auth token:",
            validationResult.error
          );
        } else {
          console.log("[useLogin] Token validated successfully");
        }

        // Log successful login
        logLoginSuccess("metamask" as WalletType, address, {
          userId: response.user?.address || "unknown",
          hasAccessToken: !!response.accessToken,
          checkSolana,
          chainId,
          tokenValidated: validationResult.valid,
          tokenValidationSkipped: Boolean(validationResult.requestFailed),
        });

        return { success: true };
      } catch (err) {
        const errorType = classifyLoginError(err);
        const message = getErrorMessage(err);

        console.error("auth error:", err);
        console.error("auth error details:", {
          name: err instanceof Error ? err.name : "unknown",
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
          signError: signError
            ? {
                name: signError.name,
                message: signError.message,
              }
            : null,
        });

        // Log different types of errors
        if (signError) {
          logMessageSign("metamask" as WalletType, false, {
            address,
            message: loginMessage,
            reason: "message_signing_failed",
            error: signError.message,
          });
        } else {
          logLoginError("metamask" as WalletType, err as Error, {
            address,
            reason: "authentication_failed",
            errorType,
            errorClass: err instanceof Error ? err.constructor.name : "unknown",
          });
        }

        // Log as JavaScript error for general error tracking
        logJavaScriptError(err as Error, "useLogin_loginMutation");

        return {
          success: false,
          errorType,
          message,
          error: err,
        };
      }
    },
    [
      authenticate,
      chainId,
      connector,
      signMessageAsync,
      solanaConnected,
      signError,
      logLoginAttempt,
      logLoginSuccess,
      logLoginError,
      logMessageSign,
      logJavaScriptError,
    ]
  );

  return {
    loginMutation,
    isMessageSigning,
    isLoading,
    error,
    solanaWarning,
    setSolanaWarning,
  };
};
